import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  addMessage,
  upsertLastAssistantMessage,
  appendThinkingStep,
  completeThinkingStep,
  finalizeThinking,
  setStreaming,
  setWaitingForReply,
  setSessionId,
  setError,
  setLimitReached,
  setQuotaStatus,
  setQuestions,
  setAiFormData,
  setAiScript,
} from "@/store/slices/aiChatSlice";
import { fetchCreditBalance } from "@/store/slices/aiCreditsSlice";
import { chat } from "@/services/aiService";

interface StreamMessage {
  type: string;
  subtype?: string;
  status?: string;
  stop_reason?: string;
  duration_ms?: number;
  message?: {
    role?: string;
    content?: Array<{ type: string; text?: string; name?: string }>;
  };
  session_id?: string;
  questions?: Array<{
    question: string;
    header: string;
    options: Array<{ label: string; description: string }>;
    multiSelect: boolean;
  }>;
  // Événements de streaming bas niveau (thinking_delta, message_delta, etc.)
  event?: {
    type: string;
    delta?: { type: string; thinking?: string; text?: string; stop_reason?: string };
  };
  // Événements de sous-agent (Agent tool)
  subagent_type?: string;
  description?: string;
  last_tool_name?: string;
}

type TFn = (key: string) => string;

function humanizeTool(toolName: string | undefined, t: TFn): string {
  if (!toolName) return t("aiAgent.tools.default");
  const map: Record<string, string> = {
    "mcp__silae-validator__validate_form_data": t("aiAgent.tools.validateFormData"),
    "mcp__silae-validator__validate_silae_syntax": t("aiAgent.tools.validateSilaeSyntax"),
    "mcp__silae-search__search_silae_kb": t("aiAgent.tools.searchKb"),
    "mcp__silae-search__lookup_variable": t("aiAgent.tools.lookupVariable"),
    "mcp__silae-search__search_by_category": t("aiAgent.tools.searchByCategory"),
  };
  return map[toolName] ?? toolName.replace(/^mcp__[^_]+__/, "").replace(/_/g, " ");
}

function humanizeAgent(subtype: string | undefined, t: TFn): string {
  const map: Record<string, string> = {
    formdata_generator: t("aiAgent.agents.formdataGenerator"),
    silae_script_generator: t("aiAgent.agents.silaeScriptGenerator"),
  };
  return map[subtype ?? ""] ?? subtype ?? t("aiAgent.agents.default");
}

export function useAgentStream() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector((state) => state.aiChat.sessionId);
  const isWaitingForReply = useAppSelector((state) => state.aiChat.isWaitingForReply);

  // Persistent reader across reply turns (same SSE stream)
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const bufferRef = useRef("");
  // Buffer le texte assistant du tour courant ; flush sur message_delta (stop_reason connu).
  const pendingTextRef = useRef<string>("");

  function handleStreamMessage(message: StreamMessage) {
    if (
      message.type === "system" &&
      message.subtype === "init" &&
      message.session_id
    ) {
      dispatch(setSessionId(message.session_id));
      return;
    }

    if (message.type === "stream_event" && message.event) {
      const ev = message.event;

      // --- Raisonnement en streaming (thinking_delta) ---
      if (ev.type === "content_block_delta" && ev.delta?.type === "thinking_delta") {
        const chunk = ev.delta.thinking ?? "";
        if (chunk) {
          dispatch(
            appendThinkingStep({
              kind: "thinking",
              label: t("aiAgent.thinking.label"),
              detail: chunk,
              coalesce: true,
            }),
          );
        }
        return;
      }

      // --- Fin de tour : flush du texte accumulé selon le stop_reason ---
      if (ev.type === "message_delta") {
        const stopReason = ev.delta?.stop_reason;
        const text = pendingTextRef.current.trim();
        pendingTextRef.current = "";
        if (text) {
          if (stopReason === "tool_use") {
            // Narration intermédiaire : va dans la timeline.
            dispatch(appendThinkingStep({ kind: "status", label: text }));
            dispatch(completeThinkingStep({ kind: "status", label: text }));
          } else {
            // Réponse finale : bulle visible.
            dispatch(upsertLastAssistantMessage(text));
          }
        }
        return;
      }

      return;
    }

    // --- Statut système (l'agent interroge le modèle) ---
    // On n'émet pas d'étape générique ici : le thinking streamé et la narration
    // (texte assistant intermédiaire) couvrent déjà l'activité de façon plus utile.
    if (message.type === "system" && message.subtype === "status") {
      // Nouveau tour : reset du buffer de texte.
      pendingTextRef.current = "";
      return;
    }

    // --- Sous-agent : démarrage ---
    if (message.type === "system" && message.subtype === "task_started") {
      dispatch(
        appendThinkingStep({
          kind: "delegate",
          label: humanizeAgent(message.subagent_type, t),
        }),
      );
      return;
    }

    // --- Sous-agent : progression (outil en cours) ---
    if (message.type === "system" && message.subtype === "task_progress") {
      if (message.last_tool_name) {
        dispatch(
          appendThinkingStep({
            kind: "tool",
            label: humanizeTool(message.last_tool_name, t),
          }),
        );
      }
      return;
    }

    // --- Sous-agent : terminé ---
    if (message.type === "system" && message.subtype === "task_notification") {
      dispatch(completeThinkingStep({ kind: "delegate" }));
      dispatch(completeThinkingStep({ kind: "tool" }));
      return;
    }

    // Le backend émet plusieurs messages `assistant` partiels pour le même tour
    // (un avec thinking, un avec text, un avec tool_use). On accumule le texte
    // dans un buffer et on flush uniquement sur message_delta (stop_reason connu).
    if (message.type === "assistant" && message.message) {
      const blocks = message.message.content ?? [];
      const chunk = blocks
        .filter((item) => item.type === "text")
        .map((item) => item.text ?? "")
        .join("");
      if (chunk) pendingTextRef.current += chunk;
      return;
    }

    if (message.type === "ask_user_question") {
      const qs = (message as any).questions;
      console.log("[useAgentStream] ask_user_question received, questions:", qs?.length);
      const header = qs?.[0]?.header ?? t("aiAgent.thinking_steps.question");
      dispatch(
        appendThinkingStep({ kind: "question", label: `Question : ${header}` }),
      );
      dispatch(setQuestions(qs));
      dispatch(setWaitingForReply(true));
      return;
    }

    if (message.type === "formdata") {
      dispatch(appendThinkingStep({ kind: "result", label: t("aiAgent.thinking_steps.formGenerated") }));
      dispatch(completeThinkingStep({ kind: "result" }));
      dispatch(
        setAiFormData({
          formData: (message as any).formData,
          generatedCode: (message as any).generatedCode ?? null,
          suggestedName: (message as any).suggestedName ?? null,
        }),
      );
      return;
    }

    if (message.type === "script") {
      dispatch(appendThinkingStep({ kind: "result", label: t("aiAgent.thinking_steps.scriptGenerated") }));
      dispatch(completeThinkingStep({ kind: "result" }));
      dispatch(setAiScript((message as any).script));
      return;
    }

    if (message.type === "quota_status") {
      dispatch(
        setQuotaStatus({
          percentUsed: (message as any).percentUsed ?? 0,
          resetAt: (message as any).resetAt ?? "",
          periodMonth: (message as any).periodMonth ?? "",
        }),
      );
      return;
    }

    if (message.type === "limit_reached") {
      const reason = (message as any).reason as string;
      const totalCostUsd = (message as any).totalCostUsd as number | undefined;
      const numTurns = (message as any).numTurns as number | undefined;
      dispatch(
        appendThinkingStep({
          kind: "result",
          label: t("aiAgent.thinking_steps.limitReached"),
        }),
      );
      dispatch(completeThinkingStep({ kind: "result" }));
      dispatch(
        setLimitReached({
          reason,
          totalCostUsd,
          numTurns,
          // Whether this is resumable is the server's call, not ours.
          resumable: (message as any).resumable ?? false,
          sessionId: (message as any).sessionId ?? null,
          resumesLeft: (message as any).resumesLeft,
        }),
      );
      dispatch(setWaitingForReply(false));
      return;
    }

    // The transcript expired in Redis (TTL) — the agent would answer with no
    // memory of the conversation. Say so instead of pretending to continue.
    if (message.type === "session_expired") {
      dispatch(setLimitReached({ reason: "session_expired", resumable: false }));
      dispatch(setWaitingForReply(false));
      dispatch(setStreaming(false));
      return;
    }

    if (message.type === "result") {
      // Fin sémantique du run — fige la timeline et débloque la saisie.
      dispatch(finalizeThinking({ durationMs: message.duration_ms }));
      dispatch(setWaitingForReply(false));
      dispatch(setStreaming(false));
      // Le coût vient d'être enregistré côté backend : si le quota mensuel
      // était dépassé, des crédits ont pu être débités — rafraîchir le solde
      // pour que la pastille du chat et la carte d'usage restent justes.
      dispatch(fetchCreditBalance());
    }
  }

  function processLines(lines: string[]) {
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const dataStr = line.slice(6);
      if (dataStr === "[DONE]") {
        dispatch(setStreaming(false));
        readerRef.current = null;
        bufferRef.current = "";
        continue;
      }
      try {
        handleStreamMessage(JSON.parse(dataStr) as StreamMessage);
      } catch {
        console.error("Failed to parse SSE message:", dataStr);
      }
    }
  }

  async function drainStream() {
    const reader = readerRef.current;
    if (!reader) return;

    const decoder = new TextDecoder();

    try {
      while (readerRef.current) {
        const { done, value } = await reader.read();
        if (done) break;

        bufferRef.current += decoder.decode(value, { stream: true });
        const lines = bufferRef.current.split("\n");
        bufferRef.current = lines[lines.length - 1];
        processLines(lines.slice(0, -1));
      }

      const tail = decoder.decode();
      if (tail) {
        bufferRef.current += tail;
        processLines(bufferRef.current.split("\n"));
        bufferRef.current = "";
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        const msg = err instanceof Error ? err.message : "Stream error";
        dispatch(setError(msg));
      }
    } finally {
      reader.cancel().catch(() => {});
      readerRef.current = null;
      dispatch(setStreaming(false));
    }
  }

  async function sendMessage(
    prompt: string,
    opts?: { isResume?: boolean },
  ) {
    const isResume = opts?.isResume ?? false;

    dispatch(setError(null));
    dispatch(setLimitReached(null));
    dispatch(setWaitingForReply(false));
    dispatch(setStreaming(true));

    // A resume carries a synthetic prompt ("continue where you left off") that
    // the user never wrote — showing it as their message would be a lie about
    // what they said. The thinking timeline already marks the restart.
    if (!isResume) {
      dispatch(
        addMessage({
          id: crypto.randomUUID(),
          role: "user",
          content: prompt,
          timestamp: Date.now(),
        }),
      );
    }

    try {
      // Always use chat() — resume session with existing sessionId when continuing
      const response = await chat(prompt, sessionId ?? undefined, isResume);

      // 429 = quota mensuel atteint, retourné par checkQuota AVANT le stream SSE
      if (response.status === 429) {
        try {
          const body = await response.json();
          dispatch(
            setLimitReached({
              reason: body?.error === "quota_exceeded" ? "quota_exceeded" : "limit",
              percentUsed: body?.percentUsed,
              resetAt: body?.resetAt,
              periodMonth: body?.periodMonth,
            }),
          );
        } catch {
          dispatch(setLimitReached({ reason: "quota_exceeded" }));
        }
        dispatch(setWaitingForReply(false));
        dispatch(setStreaming(false));
        return;
      }

      if (!response.body) throw new Error("No response body");

      readerRef.current = response.body.getReader();
      bufferRef.current = "";
      drainStream();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.error");
      dispatch(setError(msg));
      dispatch(setStreaming(false));
    }
  }

  function abortStream() {
    const reader = readerRef.current;
    if (reader) {
      reader.cancel().catch(() => {});
      readerRef.current = null;
    }
    bufferRef.current = "";
    pendingTextRef.current = "";
    dispatch(setStreaming(false));
    dispatch(setWaitingForReply(false));
    dispatch(setQuestions(null));
  }

  // Picks the conversation back up on the same session after a turn-cap stop.
  // The context lives server-side in the session transcript, so all this needs
  // to send is a nudge — the agent already knows where it was.
  function resumeConversation() {
    if (!sessionId) return;
    dispatch(
      appendThinkingStep({
        kind: "status",
        label: t("aiAgent.limit.resumeStep"),
      }),
    );
    dispatch(completeThinkingStep({ kind: "status" }));
    sendMessage(t("aiAgent.limit.resumePrompt"), { isResume: true });
  }

  return { sendMessage, resumeConversation, abortStream, isWaitingForReply };
}
